import { useContext, useEffect, useMemo, useState } from "react";
import FormField from "../../../../components/formField/formField";
import { FormFieldProps } from "../../../../components/formField/types/formFieldProps";
import KrFormContext, { EndangeredAreaGminyAttributes, KrEndangeredAreaFormTypes, krFormTypes, KrLandslideFormTypes, LandslideGeographicalLands, LandslideGminyAttributes, LandslideTectonicUnits, NavHeadingsKROEnum, NavHeadingsKRTZEum } from "../../../../contexts/krFormContext";
import SelectInput from "../../../../components/shared/selectInput/selectInput";
import { Option } from "../../../../components/shared/selectInput/types/selectInputProps";
import { styleFormField, styleFormFieldHalf } from "../../../../components/shared/stylization";
import TextAreaField from "../../../../components/textAreaField/textAreaField";
import { TextAreaFieldProps } from "../../../../components/textAreaField/types/textAreaFieldProps";
import { GetOptions, prepareDataSingleForSelect, toSelected } from "../../../../helpers/prepareForSelect";
import { createConfigValidation } from "../../../../helpers/validation/validation";
import MapSection from "./map";
import { FormSectionProps } from "./shared/types/formSectionProps";
import { ObjectType, PatternType } from "../../../../contexts/types/card";
import CollectiveSelectField from "../../collectiveEditionView/collectiveField/collectiveSelectField";
import { CollectivePreviewKey } from "../../../../helpers/collective/preview";
import { getAddCommentBtnDetails, getFieldRef, getItemRef } from "../../../../helpers/form";
import { KROFieldDict, KRTZFieldDict } from "../../shared/commentsSidebar/fieldDict";
import { notifyWarn } from "../../../../components/snackbar/snackbar";
import { CalcField } from "../../../../helpers/form/calcField";

const Location = (props: FormSectionProps) => {
    const { krForm, setkrForm } = useContext(KrFormContext);
    const [errorTipsText, setErrorTipsText] = useState<{ [key: string]: string | undefined }>({
        miejscowosC_NAZWA: '',
        gmina: '',
        powiat: '',
        wojewodztwo: '',
        numerY_DZIALEK: '',
        pL1992_X: '',
        pL1992_Y: '',
        grS80_DLUGOSC: '',
        grS80_SZEROKOSC: '',
        godlo10k: '',
        smgp50k: '',
        geographicalLands: '',
        tectonicUnits: '',
        zlewnia: '',
        lokalizacjA_OPIS: ''
    });
    const [reload, setReload] = useState(false);
    const isLandslide = props.objectType === ObjectType.KRO;

    const starkelDict: Option[] | undefined = useMemo(() => {
        const initialGeoLands = (krForm as KrLandslideFormTypes)?.geographicalLands;
        if (isLandslide) {
            const region = (krForm as KrLandslideFormTypes).landslideAttributes?.regioN_NADRZEDNY;
            if (krForm.dicts?.krainyDict && krForm.dicts?.krainyDict.length > 0) {
                return krForm.dicts?.krainyDict.map(k => {
                    return {
                        name: k.nazwa,
                        value: k.nazwa,
                        anothers: k
                    }
                })
            } else if (initialGeoLands?.length === 0) {
                if (region?.includes("Karpaty")) {
                    return GetOptions("KRAINA_GEOGRAFICZNA_STARKEL", krForm.dictionaries);
                } else if (region?.includes("Polska poza Karpatami i Sudetami")) {
                    return GetOptions("KRAINA_GEOGRAFICZNA_SOLON", krForm.dictionaries);
                }
            } else {
                const gLands = initialGeoLands;
                if (gLands && gLands.length > 0) {
                    return gLands.map(g => {
                        return {
                            name: g.nazwa,
                            value: g.nazwa
                        }
                    })
                }
            }
        }
        return [];
    }, [isLandslide, krForm])

    const tectonicDict = useMemo(() => {
        let dict = GetOptions("JEDNOSTKI_TEKTONICZNE", krForm.dictionaries, undefined, true);
        const region = (krForm as KrLandslideFormTypes).landslideAttributes?.regioN_NADRZEDNY;
        if (region) {
            var filteredDict: Option[] | undefined = [];
            if (region?.includes("Karpaty")) filteredDict.push(...dict?.filter(item => item.anothers.UWAGI === "Karpaty")!);
            if (region?.includes("Sudety")) filteredDict.push(...dict?.filter(item => item.anothers.UWAGI === "Sudety i blok przedsudecki")!);
            if (region?.includes("Polska poza Karpatami i Sudetami")) filteredDict.push(...dict?.filter(item => item.anothers.UWAGI !== "Karpaty" &&
                item.anothers.UWAGI !== "Sudety i blok przedsudecki")!);
            dict = filteredDict;
        }
        dict = dict?.sort((a: Option, b: Option) => a.name!.localeCompare(b.name!, 'pl'));
        return dict;
    }, [krForm])

    const zlewnieDict = useMemo(() => {
        let _dic: Option[] = [];
        krForm.dicts?.zlewniaDict?.forEach((zlewnia) => {
            _dic.push({
                name: zlewnia.nazwa,
                value: zlewnia.nazwa
            })
        })
        if (isLandslide && _dic.length === 0) {
            const zlewniaName = (krForm as KrLandslideFormTypes)?.landslideAttributes?.zlewnia;
            if (zlewniaName) {
                _dic.push({
                    name: zlewniaName,
                    value: zlewniaName
                })
            }
        }
        return _dic;
    }, [isLandslide, krForm])

    const is2007 = props.version === PatternType.P2007;

    var gmina = "";
    var powiat = "";
    var wojewodztwo = "";

    const isFormNotValid = krForm.config?.isValid !== undefined ? !krForm.config?.isValid : false;

    // Validation
    useEffect(() => {
        if (krForm.config?.check && !krForm.config.isValid) {
            for (const key of Object.keys(errorTipsText)) {
                setError(key);
            }
            setReload(!reload);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [krForm.config?.check])

    const setError = (key: string) => {
        const errorMsg = krForm.config?.ValidationError![key];
        if (errorMsg !== '') {
            var newErrorList = { ...errorTipsText, [key]: errorMsg };
            setErrorTipsText(newErrorList);
        }
    }

    const createFieldsForLocation = () => {
        const gminy = isLandslide ? (krForm as KrLandslideFormTypes)?.landslideGminy : (krForm as KrEndangeredAreaFormTypes)?.endangeredAreaGminy;
        _createFieldsForLocation(gminy);
    }

    const _createFieldsForLocation = (gminy?: LandslideGminyAttributes[] | EndangeredAreaGminyAttributes[] | undefined) => {
        if (gminy && gminy.length > 0) {
            let first = gminy[0];
            gmina = first.gmina ? first.gmina as string : "";
            powiat = first.powiat ? first.powiat as string : "";
            wojewodztwo = first.wojewodztwo ? first.wojewodztwo as string : "";
            for (var i = 1; i < gminy?.length; i++) {
                let next = gminy[i];
                if (next.gmina != null && !gmina.includes(next.gmina as string)) gmina += `,\n${next.gmina}`;
                if (next.powiat != null && !powiat.includes(next.powiat as string)) powiat += `,\n${next.powiat}`;
                if (next.wojewodztwo != null && !wojewodztwo.includes(next.wojewodztwo as string)) wojewodztwo += `,\n${next.wojewodztwo}`;
            }
        }
    }

    createFieldsForLocation();

    const getInputProps = (name: string, KRFieldDict: KRTZFieldDict | KROFieldDict, fieldFormName: string): FormFieldProps => {
        return {
            style: styleFormFieldHalf,
            type: "text",
            configValidation: getConfigValidation(name),
            validationError: isFormNotValid,
            disabled: true,
            tipText: errorTipsText[name],
            reload: reload,
            addCommentBtn: getAddCommentBtnDetails(props.editMode!, KRFieldDict, fieldFormName, props.setNewCommentFieldDetails!),
            inputRef: (node: any) => { getFieldRef(node, KRFieldDict, props.getMapForComment!) },
            setCommentsFilter: () => { if (props.setCommentsFilter) props.setCommentsFilter(KRFieldDict); },
            isMarkedForCommentFilter: props.markedFieldForCommentFiltering === KRFieldDict
        }
    }

    const getOtherLocationTextArea = (lForm: KrLandslideFormTypes) => {
        return {
            labelName: 'Inne dane lokalizacyjne',
            textAreaPlaceholder: lForm.landslideAttributes?.lokalizacjA_OPIS == null || lForm.landslideAttributes?.lokalizacjA_OPIS === '' ?
                'Dodaj opis' : '',
            value: lForm.landslideAttributes?.lokalizacjA_OPIS,
            textareaSharedProps: {
                className: 'body-txt-large',
                style: {
                    width: '100%',
                    minHeight: '70px'
                }
            },
            disabled: !props.editMode,
            setValue: (value: any) => {
                var form = Object.assign({}, krForm) as KrLandslideFormTypes;
                if (form && form.landslideAttributes) {
                    form.landslideAttributes.lokalizacjA_OPIS = value;
                    setkrForm(form);
                }
            },
            // validationError: isFormNotValid,
            configValidation: getConfigValidation("lokalizacjA_OPIS"),
            // tipText: errorTipsText["lokalizacjA_OPIS"],
            pristine: true,
            reload: reload,
            addCommentBtn: getAddCommentBtnDetails(props.editMode!, KROFieldDict.lok_inneDaneLokalizacyjne, "Inne dane lokalizacyjne", props.setNewCommentFieldDetails!),
            textareaRef: (node: any) => { getFieldRef(node, KROFieldDict.lok_inneDaneLokalizacyjne, props.getMapForComment!) },
            setCommentsFilter: () => { if (props.setCommentsFilter) props.setCommentsFilter(KROFieldDict.lok_inneDaneLokalizacyjne); },
            isMarkedForCommentFilter: props.markedFieldForCommentFiltering === KROFieldDict.lok_inneDaneLokalizacyjne,
            dynamicResize: true
        }
    }

    const getSmpg = (lForm: KrLandslideFormTypes) => {
        let sMGP50Ks = '';
        if (lForm.sMGP50Ks && lForm.sMGP50Ks.length > 0) {
            sMGP50Ks += `${lForm.sMGP50Ks[0].nazwa ? lForm.sMGP50Ks[0].nazwa + " " : ''}${lForm.sMGP50Ks[0].godlo}`
            for (var i = 1; i < lForm.sMGP50Ks?.length; i++) {
                sMGP50Ks += `,\n${lForm.sMGP50Ks[i].nazwa ? lForm.sMGP50Ks[i].nazwa + " " : ''}${lForm.sMGP50Ks[i].godlo}`
            }
        }
        return sMGP50Ks;
    }
    var smgp = isLandslide ? getSmpg(krForm as KrLandslideFormTypes) : "";


    const getSheets = (lForm: KrLandslideFormTypes) => {
        let sheets = '';
        if (lForm.sheets && lForm.sheets.length > 0) {
            sheets += lForm.sheets[0].godlo
            for (var j = 1; j < lForm.sheets?.length; j++) {
                sheets += `, ${lForm.sheets[j].godlo}`
            }
        }
        return sheets;
    }
    var sheets = isLandslide ? getSheets(krForm as KrLandslideFormTypes) : "";

    const miejscowoscAfterOnChange = (opt: Option[] | undefined) => {
        var form = Object.assign({}, krForm);
        const attributes = getAttributes(form);
        if (attributes) {
            if (opt) {
                const option = opt.length > 0 ? opt[0] : null;
                attributes.miejscowosC_NAZWA = option?.anothers["NAZWA"]
                attributes.miejscowosC_GID = option?.anothers["GLOBALID"]
            } else {
                attributes.miejscowosC_NAZWA = undefined;
                attributes.miejscowosC_GID = undefined;
            }
        }
        setkrForm(form);
    }

    const zlewniaAfterOnChange = (opt: Option[] | undefined) => {
        var form = Object.assign({}, krForm) as KrLandslideFormTypes;
        const attributes = getAttributes(form);
        if (attributes && form.landslideAttributes) {
            if (opt) {
                const option = opt.length > 0 ? opt[0] : null;
                form.landslideAttributes.zlewnia = option?.name;
            } else {
                form.landslideAttributes.zlewnia = undefined;
            }
        }
        setkrForm(form);
    }

    const geogprahicalLandsAfterOnChange = (opt: Option[] | undefined) => {
        var form = Object.assign({}, krForm);
        var landslideForm = form as KrLandslideFormTypes;
        if (landslideForm) {
            if (opt) {
                if (landslideForm.geographicalLands === null || landslideForm.geographicalLands === undefined) landslideForm.geographicalLands = [];
                var geographicalLands: LandslideGeographicalLands[] = [];
                for (var i = 0; i < opt?.length; i++) {
                    // eslint-disable-next-line no-loop-func
                    var element = landslideForm.geographicalLands?.find((option) => {
                        return option.nazwa === opt[i].name
                    });
                    if (element) {
                        geographicalLands.push(element);
                    } else {
                        geographicalLands.push({
                            osuwiskO_GID: landslideForm.landslideAttributes?.globalid,
                            nazwa: opt[i].name,
                            powierzchnia: opt[i].anothers.powierzchnia
                        })
                    }
                }
                landslideForm.geographicalLands = geographicalLands;
            }
            else {
                landslideForm.geographicalLands = [];
            }
            landslideForm.geographicalLands?.forEach((geogr, index) => {
                geogr.kolejnosc = index + 1;
            })
            setkrForm(landslideForm);
        }
    }

    const tectonicUnitsAfterOnChange = (opt: Option[] | undefined) => {
        var form = Object.assign({}, krForm);
        var landslideForm = form as KrLandslideFormTypes;
        if (landslideForm) {
            if (opt && validateTectonics(opt)) {
                if (landslideForm.tectonicUnits === null || landslideForm.tectonicUnits === undefined) landslideForm.tectonicUnits = [];
                var tectonicUnits: LandslideTectonicUnits[] = [];
                for (var i = 0; i < opt?.length; i++) {
                    // eslint-disable-next-line no-loop-func
                    var element = landslideForm.tectonicUnits?.find((option) => {
                        return option.opis === opt[i].name
                    });
                    if (element) {
                        tectonicUnits.push(element);
                    } else {
                        tectonicUnits.push({
                            osuwiskO_GID: landslideForm.landslideAttributes?.globalid,
                            opis: opt[i].name as string,
                            jednostkA_TEKTONICZNA_GID: opt[i].anothers["GLOBALID"]
                        })
                    }
                }
                landslideForm.tectonicUnits = tectonicUnits;
            }
            else if (!opt) {
                landslideForm.tectonicUnits = [];
            }
            landslideForm.tectonicUnits?.forEach((tect, index) => {
                tect.kolejnosc = index + 1;
            })
            setkrForm(landslideForm);
        }
    }

    const validateTectonics = (opt: Option[]) => {
        var isKarpaty = opt.find(item => item.anothers.UWAGI === "Karpaty");
        var isSudety = opt.find(item => item.anothers.UWAGI === "Sudety i blok przedsudecki");

        if (isKarpaty && isSudety) {
            notifyWarn({ message: "Brak możliwości wybrania jednostki tektonicznej Sudetów i Karpat jednocześnie" });
            return false;
        }
        return true;
    }

    const getAttributes = (form: krFormTypes) => {
        return isLandslide ? (krForm as KrLandslideFormTypes).landslideAttributes : (krForm as KrEndangeredAreaFormTypes).endangeredAreaAttributes;
    }

    const attributes = getAttributes(krForm);

    const convertDecimalToCoord = (decimalValue: number, isLongitude: boolean) => {

        if (decimalValue === null) return ""

        // Get direction
        var direction = decimalValue > 0 ? isLongitude ? "E" : "N" : isLongitude ? "W" : "S";

        // Get absolute value
        var absoluteDecimalValue = Math.abs(decimalValue);

        // Calculate degree, minutes and seconds
        var degrees = absoluteDecimalValue | 0;
        var fraction = absoluteDecimalValue - degrees;
        var minutes = (fraction * 60) | 0;
        var seconds = fraction * 3600 - minutes * 60;

        // Round seconds to 1 decimal place
        seconds = Math.round(seconds * 10) / 10;

        // Build return string, add padding to minutes and seconds if only one digit
        return `${degrees}°${minutes < 10 ? "0" : ""}${minutes}’${seconds < 10 ? "0" : ""}${seconds}’’${direction}`
    }

    const getConfigValidation = (name: string) => {
        return createConfigValidation(name, false, undefined, props.version, props.objectType);
    }

    var textAreaProps: TextAreaFieldProps = isLandslide ? getOtherLocationTextArea(krForm as KrLandslideFormTypes) : {};

    const sortLandslideLands = () => {
        var data = (krForm as KrLandslideFormTypes).geographicalLands;
        if (data) data.sort((a, b) => a.kolejnosc! - b.kolejnosc!)
        return data;
    }

    const getGminy = () => {
        var objGminy = (props.objectType === ObjectType.KRO) ? (krForm as KrLandslideFormTypes).landslideGminy : (krForm as KrEndangeredAreaFormTypes).endangeredAreaGminy;
        var gminy: any[] | undefined = objGminy?.map(item => item);
        return gminy;
    }

    const getCompleteMiejscOptions = () => {
        var options: Option[] = [];
        if (krForm.dictionaries && krForm.dictionaries["MIEJSCOWOSCI"] && krForm.dictionaries["MIEJSCOWOSCI"].length > 0) {
            var objGminy = getGminy();
            if (objGminy && objGminy.length > 0) {
                krForm.dictionaries["MIEJSCOWOSCI"].forEach(item => {
                    var gmina = objGminy!.find((elem: any) => elem.gminA_ID === item.anothers.GMI_ID && elem.gminA_ID !== null);
                    if (gmina) {
                        options.push({ value: item.value, name: `${item.name}, g. ${gmina!.gmina}`, anothers: item.anothers });
                    }
                });
            }
        }
        return options.sort((a, b) => a.name!.localeCompare(b.name!, 'pl'));
    }

    const miejscOptions = getCompleteMiejscOptions();

    const getMiejscFullName = () => {
        if (attributes?.miejscowosC_NAZWA && krForm.dictionaries && krForm.dictionaries["MIEJSCOWOSCI"] && krForm.dictionaries["MIEJSCOWOSCI"].length > 0) {
            var currentNameValue = krForm.dictionaries["MIEJSCOWOSCI"].find(item => item.anothers["GLOBALID"] === attributes.miejscowosC_GID?.toString())?.value;
            return miejscOptions.find(item => item.value === currentNameValue)?.name;
        }
        else return undefined
    }

    const tectonicSelection = useMemo(() => {
        const values = (krForm as KrLandslideFormTypes)?.tectonicUnits;
        return toSelected("jednostkA_TEKTONICZNA_GID", tectonicDict, values);
    }, [krForm, tectonicDict])

    if (props.collectiveEdition && props.objectType === ObjectType.KRTZ) {
        return <>
            <h2 style={{ width: '100%' }}>2. Lokalizacja</h2>
            <CollectiveSelectField top={true} value={getMiejscFullName()} isAuto={false} title={"Miejscowość"}
                forms={props.forms} keyName={CollectivePreviewKey.miejscowoscT} actions={props.actions} single={true}
                setActions={props.setActions} onDelete={props.onDelete}>
                <SelectInput label="Miejscowość" hasInputTags={true} options={miejscOptions} onlySingleTag={true}
                    selectedOptions={prepareDataSingleForSelect(miejscOptions, undefined, getMiejscFullName())}
                    onChange={miejscowoscAfterOnChange} />
            </CollectiveSelectField>
        </>
    }

    if (props.collectiveEdition && props.objectType === ObjectType.KRO) {
        return <>
            <h2 style={{ width: '100%' }}>2. Lokalizacja</h2>
            <CollectiveSelectField top={true} value={getMiejscFullName()} isAuto={false} title={"Miejscowość"}
                forms={props.forms} keyName={CollectivePreviewKey.miejscowoscL} actions={props.actions} single={true}
                setActions={props.setActions} onDelete={props.onDelete}>
                <SelectInput label="Miejscowość" hasInputTags={true} options={miejscOptions} onlySingleTag={true}
                    selectedOptions={prepareDataSingleForSelect(miejscOptions, undefined, getMiejscFullName())}
                    onChange={miejscowoscAfterOnChange} />
            </CollectiveSelectField>
        </>
    }

    return (
        <>
            <h2 ref={(node) => getItemRef(node, isLandslide ? NavHeadingsKROEnum.Location : NavHeadingsKRTZEum.Location,
                props.getMapForNavScroll!)}
                style={{ width: '100%' }}>2. Lokalizacja {is2007 && "osuwiska"}</h2>
            <SelectInput
                style={styleFormFieldHalf}
                label="Miejscowość"
                hasInputTags={true}
                options={miejscOptions}
                onlySingleTag={true}
                selectedOptions={prepareDataSingleForSelect(miejscOptions, undefined, getMiejscFullName())}
                onChange={miejscowoscAfterOnChange}
                pristine={true}
                configValidation={getConfigValidation("miejscowosC_NAZWA")}
                validationError={isFormNotValid}
                tipText={errorTipsText['miejscowosC_NAZWA']}
                reload={reload}
                disabled={!props.editMode}
                addCommentBtn={getAddCommentBtnDetails(props.editMode!,
                    isLandslide ? KROFieldDict.lok_miejscowosc : KRTZFieldDict.lok_miejscowosc, "Miejscowość", props.setNewCommentFieldDetails!)}
                selectRef={(node: any) => { getFieldRef(node, isLandslide ? KROFieldDict.lok_miejscowosc : KRTZFieldDict.lok_miejscowosc, props.getMapForComment!) }}
                setCommentsFilter={() => {
                    if (props.setCommentsFilter) props.setCommentsFilter(isLandslide ? KROFieldDict.lok_miejscowosc :
                        KRTZFieldDict.lok_miejscowosc);
                }}
                isMarkedForCommentFilter={props.markedFieldForCommentFiltering === (isLandslide ? KROFieldDict.lok_miejscowosc : KRTZFieldDict.lok_miejscowosc)}
            />
            <FormField {...getInputProps("gmina", isLandslide ? KROFieldDict.lok_gmina : KRTZFieldDict.lok_gmina, "Gmina")} labelName="Gmina" value={gmina} hasTextarea={true} />
            <FormField {...getInputProps("powiat", isLandslide ? KROFieldDict.lok_powiat : KRTZFieldDict.lok_powiat, "Powiat")} labelName="Powiat" value={powiat} />
            <FormField {...getInputProps("wojewodztwo", isLandslide ? KROFieldDict.lok_wojewodztwo : KRTZFieldDict.lok_wojewodztwo, "Województwo")} labelName="Województwo" value={wojewodztwo} />
            <FormField {...getInputProps("numerY_DZIALEK", isLandslide ? KROFieldDict.lok_numeryEwidencyjneDzialek : KRTZFieldDict.lok_numeryEwidencyjneDzialek, "Numery ewidencyjne działek")}
                setValue={(value: any) => {
                    if (props.objectType === ObjectType.KRO) {
                        let form = Object.assign({}, krForm) as KrLandslideFormTypes;
                        if (form && form.landslideAttributes) {
                            form.landslideAttributes.numerY_DZIALEK = value;
                            setkrForm(form);
                        }
                    }
                    else {
                        let form = Object.assign({}, krForm) as KrEndangeredAreaFormTypes;
                        if (form && form.endangeredAreaAttributes) {
                            form.endangeredAreaAttributes.numerY_DZIALEK = value;
                            setkrForm(form);
                        }
                    }
                }}
                labelName="Numery ewidencyjne działek"
                value={attributes?.numerY_DZIALEK}
                style={styleFormField} disabled={!props.editMode} />
            {isLandslide &&
                <>
                    <FormField {...getInputProps("godlo10k", KROFieldDict.lok_mapaTopograficzna10k, is2007 ? "Mapa topograficzna" : "Mapa topograficzna 1:10 000")} labelName={is2007 ? "Mapa topograficzna" : "Mapa topograficzna 1:10 000"} value={sheets} disabled={true} />
                    <FormField {...getInputProps("smgp50k", KROFieldDict.lok_arkuszSMGP50k, "Arkusz SMGP 1:50 000")} labelName="Arkusz SMGP 1:50 000" value={smgp} disabled={true} hasTextarea={true} />
                </>
            }
            {!is2007 &&
                <>
                    <p style={{ width: '100%' }}>Współrzędne płaskie prostokątne</p>
                    <FormField {...getInputProps("pL1992_X", isLandslide ? KROFieldDict.lok_wspolrzednePlaskieProstokatne_x : KRTZFieldDict.lok_wspolrzednePlaskieProstokatne_x, "Współrzędne płaskie prostokątne - x")} labelName="X" value={CalcField.toIntValue(attributes?.pL1992_X)} />
                    <FormField {...getInputProps("pL1992_Y", isLandslide ? KROFieldDict.lok_wspolrzednePlaskieProstokatne_y : KRTZFieldDict.lok_wspolrzednePlaskieProstokatne_y, "Współrzędne płaskie prostokątne - y")} labelName="Y" value={CalcField.toIntValue(attributes?.pL1992_Y)} />
                </>
            }
            {is2007 &&
                <>
                    <p style={{ width: '100%' }}>Współrzędne geograficzne</p>
                    <FormField {...getInputProps("grS80_DLUGOSC", KROFieldDict.lok_wspolrzedneGeograficzne_dlugosc, "Długość geograficzna")} labelName="Długość geograficzna" value={convertDecimalToCoord(attributes?.grS80_DLUGOSC!, true)} />
                    <FormField {...getInputProps("grS80_SZEROKOSC", KROFieldDict.lok_wspolrzedneGeograficzne_szerokosc, "Szerokość geograficzna")} labelName="Szerokość geograficzna" value={convertDecimalToCoord(attributes?.grS80_SZEROKOSC!, false)} />
                </>
            }
            {isLandslide && <>
                <SelectInput style={styleFormFieldHalf} label="Kraina geograficzna"
                    hasInputTags={true} options={starkelDict} onlySingleTag={false}
                    selectedOptions={prepareDataSingleForSelect(starkelDict, sortLandslideLands()?.map((value) => {
                        return value.nazwa;
                    }), undefined)}
                    configValidation={getConfigValidation("geographicalLands")}
                    onChange={geogprahicalLandsAfterOnChange}
                    pristine={true}
                    validationError={isFormNotValid}
                    tipText={errorTipsText["geographicalLands"]}
                    reload={reload}
                    disabled={!props.editMode}
                    addCommentBtn={getAddCommentBtnDetails(props.editMode!, KROFieldDict.lok_krainaGeograficzna, "Kraina geograficzna", props.setNewCommentFieldDetails!)}
                    selectRef={(node: any) => { getFieldRef(node, KROFieldDict.lok_krainaGeograficzna, props.getMapForComment!) }}
                    setCommentsFilter={() => { if (props.setCommentsFilter) props.setCommentsFilter(KROFieldDict.lok_krainaGeograficzna); }}
                    isMarkedForCommentFilter={props.markedFieldForCommentFiltering === KROFieldDict.lok_krainaGeograficzna}
                />
                <SelectInput style={styleFormFieldHalf} label="Jednostka tektoniczna"
                    hasInputTags={true} options={tectonicDict} onlySingleTag={false}
                    selectedOptions={tectonicSelection}
                    configValidation={getConfigValidation("tectonicUnits")}
                    onChange={tectonicUnitsAfterOnChange}
                    pristine={true}
                    validationError={isFormNotValid}
                    tipText={errorTipsText["tectonicUnits"]}
                    reload={reload}
                    disabled={!props.editMode}
                    addCommentBtn={getAddCommentBtnDetails(props.editMode!, KROFieldDict.lok_jednostkaTektoniczna, "Jednostka tektoniczna", props.setNewCommentFieldDetails!)}
                    selectRef={(node: any) => { getFieldRef(node, KROFieldDict.lok_jednostkaTektoniczna, props.getMapForComment!) }}
                    setCommentsFilter={() => { if (props.setCommentsFilter) props.setCommentsFilter(KROFieldDict.lok_jednostkaTektoniczna); }}
                    isMarkedForCommentFilter={props.markedFieldForCommentFiltering === KROFieldDict.lok_jednostkaTektoniczna}
                />
                <SelectInput style={styleFormFieldHalf} label="Zlewnia"
                    hasInputTags={true} options={zlewnieDict} onlySingleTag={true}
                    selectedOptions={prepareDataSingleForSelect(zlewnieDict, undefined, (krForm as KrLandslideFormTypes).landslideAttributes?.zlewnia)}
                    configValidation={getConfigValidation("onlyRequired")}
                    onChange={zlewniaAfterOnChange}
                    pristine={true}
                    validationError={isFormNotValid}
                    tipText={errorTipsText["zlewnia"]}
                    reload={reload}
                    disabled={!props.editMode}
                    addCommentBtn={getAddCommentBtnDetails(props.editMode!, KROFieldDict.lok_zlewnia, "Zlewnia", props.setNewCommentFieldDetails!)}
                    selectRef={(node: any) => { getFieldRef(node, KROFieldDict.lok_zlewnia, props.getMapForComment!) }}
                    setCommentsFilter={() => { if (props.setCommentsFilter) props.setCommentsFilter(KROFieldDict.lok_zlewnia); }}
                    isMarkedForCommentFilter={props.markedFieldForCommentFiltering === KROFieldDict.lok_zlewnia}
                />
                <TextAreaField {...textAreaProps} />
            </>}
            {!isLandslide && <>
                <p style={{ width: '100%' }}
                    ref={(node: any) => { getFieldRef(node, KRTZFieldDict.lok_mapaTerenu_podsekcja, props.getMapForComment!) }}
                    onClick={() => { if (props.setCommentsFilter) props.setCommentsFilter(KRTZFieldDict.lok_mapaTerenu_podsekcja); }}
                >Mapa terenu</p>
                <MapSection editMode={props.editMode} isLandslide={false} version={0} setNewCommentFieldDetails={props.setNewCommentFieldDetails}
                    getMapForComment={props.getMapForComment} setCommentsFilter={props.setCommentsFilter}
                    markedFieldForCommentFiltering={props.markedFieldForCommentFiltering} />
            </>}
        </>
    )
}

export default Location;